import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import {
    ArrowLeft,
    BookOpen,
    Clock,
    Users,
    Star,
    Play,
    FileText,
    Video,
    CheckSquare, Calendar,
    Globe,
    Award,
    Circle,
    Loader2
} from "lucide-react"
import { useGet } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"
import { courseSchema } from "../utils/schemas/cours-schema"
import { useStartCourse } from "../hooks/useCourseProgress"
import { showErrorToast, showSuccessToast } from "../utils/toast"
import AccessGuard from "../components/AccessGuard"
import { useAuth } from "../hooks/useAuth"
import { useAccessCheck } from "../hooks/useAccessCheck"
import { Badge } from "../components/ui/badge"
import CourseProgress from "../components/course/CourseProgress"
import { Button } from "../components/ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../components/Card"
import CoursePaymentModal from "../components/CoursePaymentModal"
import { formatPrice, formatDuration } from "../utils/formatters"
import { isFreeCourse } from "../utils/courseUtils"
import { saveEnrollmentIntent, getLoginUrlWithContext } from "../utils/enrollmentIntent"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "../i18n"
import { api } from "../utils/apiComponent/apiClient"
import { Languages } from "lucide-react"
import CommentsSection from "../components/course/CommentsSection"

export default function CourseDetailPage() {
  const { courseId } = useParams()
  const navigate = useNavigate()
  const { tObj, language } = useTranslation()
  // Tous les modules sont dépliés par défaut
  const [expandedModules, setExpandedModules] = useState(new Set())
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const { isAuthenticated } = useAuth()
  const [availableLanguages, setAvailableLanguages] = useState([])
  const [isCTALoading, setIsCTALoading] = useState(false)
  
  // Vérifier l'accès au cours pour obtenir le code de vérification
  const { code: accessCode } = useAccessCheck('course', courseId)

  // Utiliser le nouvel endpoint /courses/:id/details qui retourne les détails optimisés sans blocs
  const courseEndpoint = language === 'fr' 
    ? `${endpoints.getCourseDetails(courseId)}`
    : `${endpoints.getCourseDetails(courseId)}?language=${language}`

  // Fetch course details with progress and translations (sans blocs pour optimiser)
  const {
    data: courseData,
    loading: courseLoading,
    error: courseError
  } = useGet(courseEndpoint, {
    schema: courseSchema
  })

  const course = courseData
  const modules = courseData?.modules || []

  // Initialiser tous les modules comme dépliés quand les modules sont chargés
  useEffect(() => {
    if (modules.length > 0 && expandedModules.size === 0) {
      setExpandedModules(new Set(modules.map(m => m.id)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules])

  // Charger les langues disponibles du cours
  useEffect(() => {
    if (!courseId) return

    const loadCourseLanguages = async () => {
      try {
        const response = await api.get(endpoints.getCourseLanguages(courseId))
        const languages = response?.data || response || []
        // Filtrer pour ne garder que les langues supportées (fr, en)
        const supportedLanguages = languages.filter(lang => 
          typeof lang === 'string' && (lang === 'fr' || lang === 'en')
        )
        setAvailableLanguages(supportedLanguages)
      } catch (error) {
        console.debug('Error loading course languages:', error)
        // Si l'endpoint n'existe pas ou erreur, on assume que seul 'fr' est disponible
        setAvailableLanguages(['fr'])
      }
    }

    loadCourseLanguages()
  }, [courseId])

  // Hook pour démarrer un cours
  const { start: startCourse, loading: startCourseLoading } = useStartCourse()
  
  const userProgress = courseData?.userProgress
  const enrollment = courseData?.enrollment
  
  const isEnrolled = enrollment !== null && enrollment !== undefined && enrollment?.status !== undefined
  
  const currentLesson = courseData?.currentLesson || null
  const currentQuiz = courseData?.currentQuiz || null
  
  const hasRealProgress = Boolean(
    currentLesson?.id ||              
    currentQuiz?.id ||                 
    (userProgress && (
      userProgress.completedLessons > 0 ||  
      userProgress.progress > 0             
    ))
  )
  
  const isStarted = Boolean(
    courseData?.isStarted === true && 
    isEnrolled && 
    hasRealProgress
  )
  
  const hasCertificate = accessCode === 'CERTIFICATE_ISSUED' || enrollment?.status === 'COMPLETED'
  
  const isAccessExpired = accessCode === 'ACCESS_EXPIRED' || 
    (enrollment?.accessExpiresAt && new Date(enrollment.accessExpiresAt) < new Date())
  
  const completedModulesSet = new Set()
  if (courseData?.modules) {
    courseData.modules.forEach(module => {
      if (module.isCompleted === true) {
        completedModulesSet.add(module.id)
      }
    })
  }
  
  const completedLessonsCount = modules.reduce((count, module) => {
    if (module.lessons && module.lessons.length > 0) {
      const moduleCompletedLessons = module.lessons.filter(lesson => lesson.isCompleted === true).length
      return count + moduleCompletedLessons
    }
    return count
  }, 0)
  
  const totalLessonsCount = modules.reduce((count, module) => {
    if (module.lessons && module.lessons.length > 0) {
      return count + module.lessons.length
    }
    return count
  }, 0)

  const getBlockIcon = (type) => {
    switch (type) {
      case 'VIDEO':
        return <Video className="w-4 h-4 text-red-500" />
      case 'TEXT':
        return <FileText className="w-4 h-4 text-blue-500" />
      case 'ASSIGNMENT':
        return <CheckSquare className="w-4 h-4 text-green-500" />
      case 'IMAGE':
        return <Globe className="w-4 h-4 text-purple-500" />
      case 'DOWNLOAD':
        return <Award className="w-4 h-4 text-orange-500" />
      default:
        return <BookOpen className="w-4 h-4 text-gray-500" />
    }
  }

  const getTotalDuration = () => {
    // Priorité: duration du cours > calcul depuis modules
    // duration peut être en secondes ou minutes selon l'API
    if (course.duration && course.duration > 0) {
      // Si duration > 10000, on assume que c'est en secondes (plusieurs heures)
      // Sinon on assume que c'est en minutes
      return course.duration > 10000 ? course.duration : course.duration * 60
    }
    if (!modules || modules.length === 0) return 0
    const moduleDuration = modules.reduce((total, module) => total + (module.duration || 0), 0)
    // Même logique pour les modules
    return moduleDuration > 10000 ? moduleDuration : moduleDuration * 60
  }

  const getTotalBlocks = () => {
    // Compter les lessons depuis les modules (structure actuelle de l'API)
    if (!modules || modules.length === 0) return 0
    return modules.reduce((total, module) => {
      if (module.lessons && module.lessons.length > 0) {
        return total + module.lessons.length
      }
      return total
    }, 0)
  }

  const handlePaymentSuccess = () => {
    if (typeof setShowPaymentModal === 'function') {
      setShowPaymentModal(false)
    }
    showSuccessToast({ message: tObj.courseDetail.enrollmentSuccess() })
    window.location.reload()
  }

  const _getModuleButtonInfo = (module) => {
    if (!module || !module.id) {
      return { url: '', text: tObj.courseDetail.startModule(), icon: Play }
    }

    // Vérifier si le module a réellement été commencé (utilise isStarted/isCompleted de la nouvelle API)
    const moduleHasProgress = module.isStarted || module.isCompleted

    // Si le cours n'a pas été démarré OU le module n'a pas de progression, toujours afficher "Commencer le module"
    if (!isStarted || !isEnrolled || !moduleHasProgress) {
      return {
        url: `/courses/${courseId}/modules/${module.id}`,
        text: tObj.courseDetail.startModule(),
        icon: Play
      }
    }

    // Vérifier si l'utilisateur est actuellement dans ce module (quiz ou leçon)
    const isInThisModule = 
      (currentLesson?.moduleId === module.id) ||
      (currentQuiz?.moduleId === module.id)

    // Vérifier si toutes les leçons sont complétées (nouvelle structure sans blocs dans la réponse)
    const allLessonsCompleted = module.lessons && module.lessons.length > 0 && 
      module.lessons.every(lesson => lesson.isCompleted === true)

    // Si l'utilisateur est au quiz de ce module (priorité 1)
    if (currentQuiz?.moduleId === module.id) {
      return {
        url: `/courses/${courseId}/modules/${module.id}/quiz`,
        text: tObj.courseDetail.takeQuiz(),
        icon: CheckSquare
      }
    }

    // Si l'utilisateur est dans une leçon de ce module (priorité 2)
    if (currentLesson?.moduleId === module.id && currentLesson.id) {
      // Rediriger vers la leçon (le reader déterminera le bloc à afficher)
      return {
        url: `/courses/${courseId}/modules/${module.id}/lessons/${currentLesson.id}`,
        text: tObj.courseDetail.continueModule(),
        icon: Play
      }
    }

    // Si toutes les leçons sont complétées mais le quiz n'est pas passé
    if (moduleHasProgress && allLessonsCompleted && module.quiz && !module.quiz.isPassed) {
      return {
        url: `/courses/${courseId}/modules/${module.id}/quiz`,
        text: tObj.courseDetail.takeQuiz(),
        icon: CheckSquare
      }
    }

    // Si l'utilisateur a commencé le module mais n'est pas au quiz
    if (moduleHasProgress || isInThisModule) {
      // Trouver la première leçon avec un bloc non complété
      if (module.lessons && module.lessons.length > 0) {
        for (const lesson of module.lessons) {
          const firstIncompleteBlock = lesson.blocks?.find(block => 
            !block.isCompleted && block.progress?.status !== 'COMPLETED'
          )
          
          if (firstIncompleteBlock) {
            return {
              url: `/courses/${courseId}/modules/${module.id}/lessons/${lesson.id}/blocks/${firstIncompleteBlock.id}`,
              text: tObj.courseDetail.continueModule(),
              icon: Play
            }
          }
        }
      }
      
      // Fallback pour l'ancienne structure (blocs directement dans le module)
      const firstIncompleteBlock = module.blocks?.find(block => 
        !block.isCompleted && block.progress?.status !== 'COMPLETED'
      )
      
      if (firstIncompleteBlock) {
        return {
          url: `/courses/${courseId}/modules/${module.id}/blocks/${firstIncompleteBlock.id}`,
          text: tObj.courseDetail.continueModule(),
          icon: Play
        }
      }

      // Si tous les blocs sont complétés, rediriger vers le quiz ou le module
      if (module.quiz?.quiz) {
        return {
          url: `/courses/${courseId}/modules/${module.id}/quiz`,
          text: module.quiz.isPassed ? tObj.courseDetail.retakeQuizButton() : tObj.courseDetail.takeQuiz(),
          icon: CheckSquare
        }
      }

      return {
        url: `/courses/${courseId}/modules/${module.id}`,
        text: tObj.courseDetail.continueModule(),
        icon: Play
      }
    }

    // Par défaut : commencer le module
    return {
      url: `/courses/${courseId}/modules/${module.id}`,
      text: tObj.courseDetail.startModule(),
      icon: Play
    }
  }

  // Fonction pour obtenir l'URL de navigation selon la progression
  // Utilise currentLesson et currentQuiz de la nouvelle API
  const getCourseNavigationUrl = () => {
    // Ne considérer la progression que si le cours est démarré
    if (!isStarted || !isEnrolled) {
      // Si pas démarré, retourner le premier module
      if (modules && modules.length > 0) {
        const sortedModules = [...modules].sort((a, b) => (a.order || 0) - (b.order || 0))
        const firstModule = sortedModules[0]
        if (firstModule?.id) {
          return `/courses/${courseId}/modules/${firstModule.id}`
        }
      }
      return `/courses/${courseId}`
    }
    
    // Priorité 1: currentQuiz (utilisateur doit passer un quiz)
    if (currentQuiz?.id && currentQuiz?.moduleId) {
      return `/courses/${courseId}/modules/${currentQuiz.moduleId}/quiz`
    }
    
    // Priorité 2: currentLesson (leçon actuelle où l'utilisateur en est)
    if (currentLesson?.id && currentLesson?.moduleId) {
      return `/courses/${courseId}/modules/${currentLesson.moduleId}/lessons/${currentLesson.id}`
    }
    
    // Priorité 3: Premier module (par ordre)
    if (modules && modules.length > 0) {
      const sortedModules = [...modules].sort((a, b) => (a.order || 0) - (b.order || 0))
      const firstModule = sortedModules[0]
      if (firstModule?.id) {
        return `/courses/${courseId}/modules/${firstModule.id}`
      }
    }
    
    // Fallback: page du cours
    return `/courses/${courseId}`
  }

  // Fonction pour obtenir le texte et l'action du CTA principal
  // Logique robuste avec vérifications strictes
  const getMainCTAInfo = () => {
    // 1. Vérifier l'authentification
    if (!isAuthenticated) {
      return {
        text: tObj.courseDetail.enrollInCourse(),
        icon: Play,
        action: handleStartCourse
      }
    }

    // 2. Vérifier l'enrollment
    if (!isEnrolled) {
      return {
        text: tObj.courseDetail.enrollInCourse(),
        icon: Play,
        action: handleStartCourse
      }
    }

    // 3. Vérifier strictement si le cours a été démarré avec progression réelle
    // Si le cours n'est pas démarré OU s'il n'y a pas de progression réelle, afficher "Start course"
    if (!isStarted) {
      return {
        text: tObj.courseDetail.startCourse(),
        icon: Play,
        action: handleStartCourse
      }
    }

    // 4. Cours démarré avec progression réelle - déterminer l'action selon la progression
    // Vérifier que les données existent et sont valides avant de les utiliser
    
    // Priorité 1: currentQuiz (utilisateur doit passer un quiz)
    if (currentQuiz?.id && currentQuiz?.moduleId) {
      return {
        text: tObj.courseDetail.takeQuiz(),
        icon: CheckSquare,
        action: () => {
          const url = `/courses/${courseId}/modules/${currentQuiz.moduleId}/quiz`
          window.location.href = url
        }
      }
    }

    // Priorité 2: currentLesson (leçon actuelle où l'utilisateur en est)
    if (currentLesson?.id && currentLesson?.moduleId) {
      return {
        text: tObj.courseDetail.continueCourse(),
        icon: Play,
        action: () => {
          const url = `/courses/${courseId}/modules/${currentLesson.moduleId}/lessons/${currentLesson.id}`
          window.location.href = url
        }
      }
    }

    // Priorité 3: Premier module disponible
    if (modules && modules.length > 0) {
      const sortedModules = [...modules].sort((a, b) => (a.order || 0) - (b.order || 0))
      const firstModule = sortedModules[0]
      if (firstModule?.id) {
        return {
          text: tObj.courseDetail.startCourse(),
          icon: Play,
          action: () => {
            const url = `/courses/${courseId}/modules/${firstModule.id}`
            window.location.href = url
          }
        }
      }
    }
    
    // Dernier fallback: démarrer le cours
    return {
      text: tObj.courseDetail.startCourse(),
      icon: Play,
      action: handleStartCourse
    }
  }

  const handleStartCourse = async () => {
    // Validation des prérequis
    if (!courseId) {
      showErrorToast({ message: tObj.courseDetail.errorMessage() })
      return
    }

    if (!course || !course.id) {
      showErrorToast({ message: tObj.courseDetail.courseNotFound() })
      return
    }
    
    // Si l'utilisateur n'est pas connecté, ouvrir le modal d'authentification
    if (!isAuthenticated) {
      // Sauvegarder l'intention d'enrollment
      saveEnrollmentIntent({
        courseId: course.id,
        courseTitle: course.title,
        courseThumbnail: course.thumbnail,
        isFree: isFreeCourse(course),
        returnUrl: window.location.pathname
      })
      
      // Ouvrir le modal d'authentification
      setShowAuthModal(true)
      return
    }

    // Si l'utilisateur n'est pas enrôlé, gérer l'inscription
    if (!isEnrolled) {
      // Vérifier que le cours existe et est valide
      if (!course || !course.id) {
        showErrorToast({ message: tObj.courseDetail.courseNotFound() })
        return
      }

      // Si c'est un cours payant, déclencher le processus de paiement
      if (!isFreeCourse(course)) {
        // Vérifier que le cours a un prix valide
        if (!course.price || parseFloat(course.price) <= 0) {
          showErrorToast({ message: tObj.courseDetail.errorMessage() })
          return
        }
        setShowPaymentModal(true)
        return
      }
      
      // Pour les cours gratuits, enrollment automatique
      setIsCTALoading(true)
      try {
        await api.get(endpoints.courseEnroll(courseId))
        showSuccessToast({ message: tObj.courseDetail.enrollmentSuccess() })
        // Recharger la page pour mettre à jour les données
        window.location.reload()
        return
      } catch (err) {
        setIsCTALoading(false)
        // Si erreur 400, peut être déjà enrôlé
        if (err?.status === 400) {
          window.location.reload()
          return
        }
        const errorMessage = err?.response?.data?.message || err?.message || tObj.courseDetail.enrollmentError()
        showErrorToast({ message: errorMessage })
        return
      }
    }

    // Si le cours n'est pas encore démarré, lancer startCourse
    if (!isStarted) {
      setIsCTALoading(true)
      try {
        await startCourse(courseId)
        setIsCTALoading(false)
        
        // Après avoir démarré le cours, rediriger vers le contenu
        // Utiliser currentLesson ou currentQuiz de la nouvelle API
        const navigationUrl = getCourseNavigationUrl()
        if (navigationUrl && navigationUrl !== `/courses/${courseId}`) {
          window.location.href = navigationUrl
        } else {
          // Fallback: recharger la page si aucune URL de redirection
          window.location.reload()
        }
        return
      } catch (err) {
        setIsCTALoading(false)
        // Si erreur 403 : utilisateur non enrôlé
        if (err?.status === 403) {
          if (!isFreeCourse(course)) {
            setShowPaymentModal(true)
          } else {
            showErrorToast({ message: tObj.courseDetail.youMustBeEnrolled() })
          }
          return
        } else if (err?.status === 400) {
          // Cours déjà démarré, rediriger vers le contenu
          const navigationUrl = getCourseNavigationUrl()
          if (navigationUrl && navigationUrl !== `/courses/${courseId}`) {
            window.location.href = navigationUrl
          } else {
            window.location.reload()
          }
          return
        } else {
          const errorMessage = err?.response?.data?.message || err?.message || tObj.courseDetail.enrollmentError()
          showErrorToast({ message: errorMessage })
          return
        }
      }
    }
    
    // Cours déjà démarré - rediriger selon la progression
    const navigationUrl = getCourseNavigationUrl()
    if (navigationUrl && navigationUrl !== `/courses/${courseId}`) {
      window.location.href = navigationUrl
    }
  }

  const handleEnrollmentSuccess = () => {
    showSuccessToast({ message: tObj.courseDetail.enrollmentSuccess() })
    // Recharger la page pour mettre à jour les données
    window.location.reload()
  }

  if (courseLoading) {
    return (
      <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#203a8a] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">{tObj.courseDetail.loading()}</p>
        </div>
      </div>
    )
  }

  if (courseError) {
      return (
        <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Erreur</span>
            <h2 className="font-display text-3xl text-[#203a8a] mt-2 mb-3">{tObj.courseDetail.errorTitle()}</h2>
            <p className="text-gray-600 mb-4">
              {courseError?.message || tObj.courseDetail.errorMessage()}
            </p>
          <Link to="/courses">
            <Button>{tObj.courseDetail.backToCourses()}</Button>
          </Link>
        </div>
      </div>
    )
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">404</span>
          <h2 className="font-display text-3xl text-[#203a8a] mt-2 mb-3">{tObj.courseDetail.courseNotFound()}</h2>
          <p className="text-gray-600 mb-4">{tObj.courseDetail.courseNotFoundDesc()}</p>
          <Link to="/courses">
            <Button>{tObj.courseDetail.backToCourses()}</Button>
          </Link>
        </div>
      </div>
    )
  }

  // La page est accessible même sans connexion
  // AccessGuard gère l'authentification et l'enrollment de manière centralisée
  return (
    <AccessGuard
      resourceType="course"
      resourceId={courseId}
      course={course}
        showLoading={courseLoading}
      redirectOnBlock={false}
      onEnrollmentSuccess={handleEnrollmentSuccess}
      onPaymentRequest={() => setShowPaymentModal(true)}
    >
    <div className="min-h-screen bg-[#FAFBFD] text-[#1B2240] font-sans">
      {/* Header éditorial — back link */}
      <div className="bg-[#FAFBFD] border-b border-[#D5DAE3] sticky top-16 z-30">
        <div className="edito-container py-4">
          <Link
            to="/courses"
            className="group inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#6B7280] hover:text-[#203a8a] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
            <span className="font-medium">{tObj.courseDetail.backToCourses()}</span>
          </Link>
        </div>
      </div>

      {/* ============================================================
       *  COURSE HERO — full-bleed, contenu + panneau prix/CTA intégré
       * ============================================================ */}
      <div className="relative bg-[#1B2240] overflow-hidden">
        {(course.thumbnail || course.banner) && (
          <img
            src={course.thumbnail || course.banner}
            alt={course.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Overlay marine */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1430]/95 via-[#1B2240]/70 to-[#0d1430]/55" />
        {/* Texture pointillée */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-20">
          <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end">
            {/* COLONNE GAUCHE — Titre / desc / stats */}
            <div className="col-span-12 lg:col-span-7 xl:col-span-8">
              {/* Eyebrow + badges */}
              <div className="flex items-center gap-3 mb-5 flex-wrap">
                <span className="h-px w-10 bg-[#E8C547]" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#E8C547]/90">
                  {course.category?.name || tObj.courseDetail.category()}
                </span>
                <span className="text-white/30">·</span>
                <span className="bg-white/10 border border-white/20 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.22em] px-2.5 py-1">
                  {course.level === "BEGINNER" ? tObj.courseDetail.beginner() :
                   course.level === "INTERMEDIATE" ? tObj.courseDetail.intermediate() :
                   course.level === "ADVANCED" ? tObj.courseDetail.advanced() : course.level}
                </span>
              </div>

              {/* Titre Fraunces XXL */}
              <h1 className="font-display tracking-tight text-balance leading-[0.98] text-[clamp(2rem,4.8vw,4rem)] course-hero-title drop-shadow-[0_4px_30px_rgba(0,0,0,0.5)]">
                {course.title}
              </h1>

              {/* Description */}
              {(course.shortDescription || course.description) && (
                <p className="font-sans mt-5 text-white/85 text-base sm:text-lg lg:text-xl max-w-2xl leading-relaxed line-clamp-3">
                  {course.shortDescription || course.description}
                </p>
              )}

              {/* Stats hairline */}
              <div className="mt-7 pt-5 border-t border-white/15 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/85">
                <span className="inline-flex items-center gap-2 text-sm">
                  <Star className="w-4 h-4 fill-[#E8C547] text-[#E8C547]" />
                  <span className="font-medium">{course.rating || "4.5"}</span>
                  <span className="text-white/50 text-xs uppercase tracking-[0.22em]">
                    ({course.totalRatings || "0"} {tObj.courseDetail.reviews()})
                  </span>
                </span>
                <span className="w-px h-4 bg-white/20" />
                <span className="inline-flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-[#E8C547]" />
                  <span className="font-medium">
                    {(course._count?.enrollments ?? course.currentStudents ?? 0)}
                  </span>
                  <span className="text-white/50 text-xs uppercase tracking-[0.22em]">
                    {tObj.courseDetail.students()}
                  </span>
                </span>
                <span className="w-px h-4 bg-white/20" />
                <span className="inline-flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-[#E8C547]" />
                  <span className="font-medium">{formatDuration(getTotalDuration(), 'seconds')}</span>
                </span>
                <span className="w-px h-4 bg-white/20" />
                <span className="inline-flex items-center gap-2 text-sm">
                  <BookOpen className="w-4 h-4 text-[#E8C547]" />
                  <span className="font-medium">{modules.length} {tObj.courseDetail.modules()}</span>
                </span>
                <span className="w-px h-4 bg-white/20" />
                <span className="inline-flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-[#E8C547]" />
                  <span className="font-medium">{getTotalBlocks()} {tObj.courseDetail.lessons()}</span>
                </span>
              </div>
            </div>

            {/* COLONNE DROITE — Panneau prix / CTA */}
            <div className="col-span-12 lg:col-span-5 xl:col-span-4">
              <div className="bg-white/95 backdrop-blur-md border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] p-6 sm:p-7">
                {/* Bandeau certificat obtenu */}
                {hasCertificate && (
                  <div className="mb-5 pb-5 border-b border-[#D5DAE3]">
                    <div className="flex items-start gap-3">
                      <Award className="w-6 h-6 text-[#E8C547] flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-[10px] uppercase tracking-[0.28em] text-[#203a8a]">Félicitations</span>
                        <p className="font-display text-lg text-[#203a8a] leading-tight mt-0.5">
                          {tObj.courseDetail.certificateObtained()}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate('/profile?tab=achievements')}
                      className="w-full bg-[#203a8a] hover:bg-[#1a2f6e] text-white mt-4 text-xs uppercase tracking-[0.22em] py-3 rounded-none"
                    >
                      <Award className="w-4 h-4 mr-2" />
                      {tObj.courseDetail.viewMyCertificate()}
                    </Button>
                  </div>
                )}

                {/* Bandeau accès expiré */}
                {isAccessExpired && !hasCertificate && (
                  <div className="mb-5 pb-5 border-b border-[#D5DAE3]">
                    <div className="flex items-start gap-3 mb-3">
                      <Clock className="w-5 h-5 text-[#E8A547] flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-[10px] uppercase tracking-[0.28em] text-[#E8A547]">Accès</span>
                        <p className="font-display text-lg text-[#1B2240] mt-0.5">{tObj.courseDetail.accessExpired()}</p>
                        <p className="text-xs text-[#1B2240]/70 mt-1">{tObj.courseDetail.accessExpiredDesc()}</p>
                      </div>
                    </div>
                    {!isFreeCourse(course) && (
                      <Button
                        onClick={() => setShowPaymentModal(true)}
                        className="w-full bg-[#203a8a] hover:bg-[#1a2f6e] text-white text-xs uppercase tracking-[0.22em] rounded-none"
                      >
                        {tObj.courseDetail.renewAccess()}
                      </Button>
                    )}
                  </div>
                )}

                {/* Prix (uniquement si pas inscrit et pas expiré) */}
                {!isEnrolled && !hasCertificate && !isAccessExpired && (
                  <div className="mb-5">
                    <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">
                      {isFreeCourse(course) ? 'Accès' : 'Investissement'}
                    </span>
                    <div className="flex items-end gap-3 mt-1">
                      <div className="font-display text-4xl sm:text-5xl text-[#203a8a] leading-none">
                        {isFreeCourse(course) ? tObj.courseDetail.free() : formatPrice(course.price, course.currency)}
                      </div>
                      {course.originalPrice &&
                       parseInt(course.originalPrice) > 0 &&
                       parseInt(course.originalPrice) > parseInt(course.price || 0) && (
                        <div className="text-sm text-[#9CA3AF] line-through pb-1">
                          {formatPrice(course.originalPrice, course.currency)}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Progression (inscrit) */}
                {isEnrolled && userProgress && !hasCertificate && !isAccessExpired && (
                  <div className="mb-5">
                    <div className="flex items-end justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">
                        {tObj.courseDetail.yourProgress()}
                      </span>
                      <span className="font-display text-2xl text-[#203a8a] leading-none">
                        {(userProgress?.progress ?? 0).toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-[#F1F3F8] h-1.5">
                      <div
                        className={`h-1.5 transition-all duration-700 ${
                          enrollment?.status === 'COMPLETED' ? 'bg-[#E8C547]' : 'bg-[#203a8a]'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, userProgress?.progress ?? 0))}%` }}
                      />
                    </div>
                    <p className="text-xs text-[#6B7280] mt-2">
                      {userProgress?.completedLessons ?? 0} {tObj.courseDetail.lessonsCompleted()} {userProgress?.totalLessons ?? 0}
                    </p>
                  </div>
                )}

                {/* CTA principal */}
                {!hasCertificate && !isAccessExpired && (() => {
                  const ctaInfo = getMainCTAInfo()
                  const CTAIcon = ctaInfo.icon
                  const isLoading = isCTALoading || startCourseLoading
                  return (
                    <Button
                      onClick={ctaInfo.action}
                      disabled={isLoading}
                      className={`w-full text-xs uppercase tracking-[0.22em] py-3.5 rounded-none ${
                        isEnrolled
                          ? 'bg-[#203a8a] hover:bg-[#1a2f6e] text-white'
                          : 'bg-[#E8C547] hover:bg-[#d4af3a] text-[#203a8a]'
                      } ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {tObj.courseDetail.starting()}
                        </>
                      ) : (
                        <>
                          <CTAIcon className="w-4 h-4 mr-2" />
                          {ctaInfo.text}
                        </>
                      )}
                    </Button>
                  )
                })()}

                {/* Mentions sous CTA */}
                {!isEnrolled && !hasCertificate && !isAccessExpired && (
                  <div className="mt-4 pt-4 border-t border-[#D5DAE3] space-y-1.5 text-xs text-[#6B7280]">
                    <p className="flex items-center gap-2">
                      <CheckSquare className="w-3.5 h-3.5 text-[#E8C547]" />
                      {tObj.courseDetail.lifetimeAccess()}
                    </p>
                    <p className="flex items-center gap-2">
                      <Award className="w-3.5 h-3.5 text-[#E8C547]" />
                      {tObj.courseDetail.completionCertificate()}
                    </p>
                  </div>
                )}

                {isEnrolled && !hasCertificate && !isAccessExpired && (
                  <p className="mt-4 pt-4 border-t border-[#D5DAE3] flex items-center justify-center gap-2 text-xs uppercase tracking-[0.22em] text-[#203a8a] font-semibold">
                    <CheckSquare className="w-4 h-4 text-[#E8C547]" />
                    {tObj.courseDetail.youAreEnrolled()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {/* Course Info - Left Column */}
          <div className="lg:col-span-2">
            {/* Course Description éditoriale */}
            {course.description && (
              <div className="bg-white border border-[#D5DAE3] p-6 sm:p-8 mb-6">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">
                  À propos
                </span>
                <h2 className="font-display text-2xl sm:text-3xl text-[#203a8a] mt-2 mb-5 leading-tight">
                  {tObj.courseDetail.description()}
                </h2>
                <p className="text-base text-[#1B2240]/80 leading-relaxed">{course.description}</p>
              </div>
            )}

            {/* Sections importantes sur mobile - juste après la description */}
            {/* Sur mobile, ces sections apparaissent ici, sur desktop elles restent dans la sidebar */}
            <div className="lg:hidden space-y-4 sm:space-y-6 mb-4 sm:mb-6">
              {/* Message de félicitations si certificat obtenu - Mobile */}
              {hasCertificate && (
                <div className="p-5 bg-[#E8C547]/15 border border-[#E8C547]">
                  <div className="flex items-start gap-3 mb-3">
                    <Award className="w-6 h-6 text-[#203a8a] flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] uppercase tracking-[0.28em] text-[#203a8a]">Félicitations</span>
                      <h3 className="font-display text-xl text-[#203a8a] mt-0.5">{tObj.courseDetail.congratulations()}</h3>
                      <p className="text-sm text-[#1B2240]/80 mt-1">{tObj.courseDetail.certificateObtained()}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/profile?tab=achievements')}
                    className="w-full bg-[#203a8a] hover:bg-[#1a2f6e] text-white mt-3 text-xs uppercase tracking-[0.22em] py-3 rounded-none"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    <span>{tObj.courseDetail.viewMyCertificate()}</span>
                  </Button>
                </div>
              )}

              {/* Course Info - Mobile */}
              <Card className="border border-[#D5DAE3] shadow-none rounded-none bg-white">
                <CardHeader className="p-5 sm:p-5 md:p-6 pb-4 sm:pb-4">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Infos</span>
                  <CardTitle className="font-display text-xl text-[#203a8a] mt-1">{tObj.courseDetail.courseInfo()}</CardTitle>
                </CardHeader>
                <CardContent className="p-5 md:p-6 pt-0">
                  <ul className="divide-y divide-[#D5DAE3] border-t border-[#D5DAE3]">
                    {availableLanguages.length > 0 && (
                      <li className="flex items-start gap-3 py-3">
                        <Languages className="w-4 h-4 text-[#E8C547] mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B7280]">
                            {tObj.courseDetail.availableLanguages()}
                          </span>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {availableLanguages.map((lang) => (
                              <span
                                key={lang}
                                className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 border border-[#D5DAE3] text-[#1B2240]"
                              >
                                {lang === 'fr' ? 'FR' : lang === 'en' ? 'EN' : lang.toUpperCase()}
                              </span>
                            ))}
                          </div>
                        </div>
                      </li>
                    )}
                    <li className="flex items-center gap-3 py-3 text-sm">
                      <Calendar className="w-4 h-4 text-[#E8C547] flex-shrink-0" />
                      <span className="text-[#1B2240]/80">{tObj.courseDetail.lastUpdate()}{new Date(course.updatedAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                    </li>
                    <li className="flex items-center gap-3 py-3 text-sm">
                      <Award className="w-4 h-4 text-[#E8C547] flex-shrink-0" />
                      <span className="text-[#1B2240]/80">{tObj.courseDetail.certificateIncluded()}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Course Progress - Afficher seulement si enrôlé */}
            {isEnrolled && modules.length > 0 && (
              <CourseProgress
                modules={modules}
                currentModuleId={null}
                onModuleSelect={() => {}}
                completedModules={completedModulesSet}
                userProgress={userProgress}
              />
            )}
            
            {/* Progress Info - Afficher seulement si enrôlé */}
            {isEnrolled && userProgress && (
              <div className="bg-white border border-[#D5DAE3] p-6 mb-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Avancement</span>
                    <h2 className="font-display text-2xl text-[#203a8a] mt-1">{tObj.courseDetail.myProgress()}</h2>
                  </div>
                  <span className="bg-[#203a8a] text-white text-[10px] uppercase tracking-[0.22em] px-2.5 py-1">
                    {enrollment?.status === 'COMPLETED' ? tObj.courseDetail.completed() : tObj.courseDetail.inProgress()}
                  </span>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{tObj.courseDetail.globalProgress()}</span>
                      <span className="font-display text-2xl text-[#203a8a]">
                        {(userProgress?.progress ?? 0).toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-[#F1F3F8] h-2 overflow-hidden">
                      <div
                        className={`h-2 transition-all duration-700 ${
                          enrollment?.status === 'COMPLETED' ? 'bg-[#E8C547]' : 'bg-[#203a8a]'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, userProgress?.progress ?? 0))}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-2 border-t">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">{tObj.courseDetail.completedLessons()}</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900">
                        {completedLessonsCount}
                        <span className="text-xs sm:text-sm font-normal text-gray-500">/{totalLessonsCount}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">{tObj.courseDetail.completedModules()}</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900">
                        {completedModulesSet.size}
                        <span className="text-xs sm:text-sm font-normal text-gray-500">/{modules.length}</span>
                      </p>
                    </div>
                  </div>
                  {userProgress?.lastAccessedAt && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 pt-2 border-t">
                      <Clock className="w-4 h-4" />
                      <span>
                        {tObj.courseDetail.lastVisit()} {new Date(userProgress.lastAccessedAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                  {enrollment?.enrolledAt && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {tObj.courseDetail.enrolledOn()} {new Date(enrollment.enrolledAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Course Modules */}
            <div className="bg-white border border-[#D5DAE3] p-6 sm:p-8 mt-6">
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Programme</span>
              <h2 className="font-display text-2xl sm:text-3xl text-[#203a8a] mt-2 mb-6 leading-tight">
                {tObj.courseDetail.moduleDetails()}{' '}
                <span className="text-[#1B2240]/60 italic font-light">
                  ({modules.length} {tObj.courseDetail.modules()})
                </span>
              </h2>
              
              {modules.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {tObj.courseDetail.noModulesAvailable()}
                </p>
              ) : (
                <div className="space-y-4">
                  {modules.map((module, index) => {
                    const moduleProgress = module.progress
                    // Utiliser isCompleted et isStarted depuis l'API /details
                    const isCompleted = module.isCompleted || moduleProgress?.isCompleted || false
                    const isStarted = module.isStarted || false
                    const progressPercentage = moduleProgress?.moduleProgress || 0
                    // Compter les leçons dans le module (nouvelle structure)
                    // Les blocs ne sont pas inclus dans la réponse de /details
                    const totalBlocks = module.lessons?.length || moduleProgress?.totalBlocks || module._count?.blocks || 0
                    
                    return (
                      <Card key={module.id} className={`shadow-none rounded-none border bg-white ${
                        isCompleted ? 'border-[#E8C547] bg-[#E8C547]/5' :
                        isStarted ? 'border-[#203a8a] bg-[#203a8a]/5' :
                        'border-[#D5DAE3]'
                      }`}>
                        <CardHeader
                          className="cursor-pointer hover:bg-[#FAFBFD] transition-colors p-5 sm:p-6"
                          onClick={() => {
                            const newExpanded = new Set(expandedModules)
                            if (newExpanded.has(module.id)) {
                              newExpanded.delete(module.id)
                            } else {
                              newExpanded.add(module.id)
                            }
                            setExpandedModules(newExpanded)
                          }}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4 flex-1 min-w-0">
                              {/* Numéro Fraunces */}
                              <div className="flex-shrink-0 w-12 text-center">
                                <span className={`font-display text-3xl sm:text-4xl leading-none ${
                                  isCompleted ? 'text-[#E8C547]' :
                                  isStarted ? 'text-[#203a8a]' :
                                  'text-[#1B2240]/30'
                                }`}>
                                  {String(index + 1).padStart(2, '0')}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  {isCompleted && (
                                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#E8C547] font-semibold inline-flex items-center gap-1">
                                      <CheckSquare className="w-3 h-3" />
                                      {tObj.courseDetail.completed()}
                                    </span>
                                  )}
                                  {!isCompleted && isStarted && (
                                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#203a8a] font-semibold inline-flex items-center gap-1">
                                      <Play className="w-3 h-3" />
                                      {tObj.courseDetail.inProgress()}
                                    </span>
                                  )}
                                </div>
                                <CardTitle className="font-display text-xl sm:text-2xl text-[#203a8a] leading-tight">
                                  {module.title}
                                </CardTitle>
                                {module.description && (
                                  <p className="text-[#1B2240]/70 text-sm mt-2 line-clamp-2">{module.description}</p>
                                )}
                                {/* Module Progress */}
                                {moduleProgress && userProgress && (
                                  <div className="mt-3">
                                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-1">
                                      <span>{tObj.courseDetail.progression()}</span>
                                      <span className="font-display text-base text-[#203a8a] tracking-normal">{progressPercentage.toFixed(0)}%</span>
                                    </div>
                                    <div className="w-full bg-[#F1F3F8] h-1">
                                      <div
                                        className={`h-1 transition-all duration-700 ${
                                          isCompleted ? 'bg-[#E8C547]' : 'bg-[#203a8a]'
                                        }`}
                                        style={{ width: `${progressPercentage}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right text-xs text-[#6B7280] flex-shrink-0">
                              <div className="flex items-center gap-1 justify-end">
                                <Clock className="w-3.5 h-3.5" />
                                <span>{formatDuration(module.duration, 'minutes')}</span>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                      
                      {expandedModules.has(module.id) && (
                        <CardContent className="pt-0 p-5 sm:p-6">
                          <div className="border-t border-[#D5DAE3] pt-5">
                            <div className="flex items-center justify-between mb-4">
                              <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">{tObj.courseDetail.lessonsTitle()}</span>
                              <div className="flex items-center gap-2">
                                {/* Bouton séparé pour le quiz si l'utilisateur n'est pas déjà au quiz et a démarré le cours ET le module a de la progression */}
                                {(() => {
                                  const moduleHasProgress = module.isStarted || module.isCompleted
                                  
                                  return isStarted && isEnrolled && moduleHasProgress && module.quiz && 
                                    currentQuiz?.moduleId !== module.id && 
                                    currentLesson?.moduleId !== module.id && (
                                    <Link to={`/courses/${courseId}/modules/${module.id}/quiz`}>
                                      {/* <Button size="sm" variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                                        <CheckSquare className="w-4 h-4 mr-2" />
                                        {module.quiz.isPassed ? tObj.courseDetail.retakeQuizButton() : tObj.courseDetail.takeQuiz()}
                                      </Button> */}
                                    </Link>
                                  )
                                })()}
                              </div>
                            </div>
                            <div className="space-y-4">
                              {(module.lessons || []).map((lesson) => {
                                const lessonBlocks = lesson.blocks || []
                                const completedBlocksInLesson = lessonBlocks.filter(block => 
                                  block.isCompleted || block.progress?.status === 'COMPLETED'
                                ).length
                                const totalBlocksInLesson = lessonBlocks.length
                                // Utiliser isCompleted et isStarted depuis l'API /details
                                const isLessonCompleted = lesson.isCompleted || false
                                const isLessonStarted = lesson.isStarted || false
                                
                                return (
                                  <div key={lesson.id} className={`border-l-2 pl-4 ${
                                    isLessonCompleted ? 'border-l-[#E8C547]' :
                                    isLessonStarted ? 'border-l-[#203a8a]' :
                                    'border-l-[#D5DAE3]'
                                  }`}>
                                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <BookOpen className={`w-4 h-4 ${
                                          isLessonCompleted ? 'text-[#E8C547]' :
                                          isLessonStarted ? 'text-[#203a8a]' :
                                          'text-[#203a8a]/50'
                                        }`} />
                                        <h5 className="font-display text-base text-[#203a8a]">
                                          {lesson.title}
                                        </h5>
                                        {isLessonCompleted && (
                                          <span className="text-[10px] uppercase tracking-[0.22em] text-[#E8C547] font-semibold inline-flex items-center gap-1">
                                            <CheckSquare className="w-3 h-3" />
                                            {tObj.courseDetail.completed()}
                                          </span>
                                        )}
                                        {!isLessonCompleted && isLessonStarted && (
                                          <span className="text-[10px] uppercase tracking-[0.22em] text-[#203a8a] font-semibold inline-flex items-center gap-1">
                                            <Play className="w-3 h-3" />
                                            {tObj.courseDetail.inProgress()}
                                          </span>
                                        )}
                                        {lesson.description && (
                                          <span className="text-xs text-[#6B7280] italic">— {lesson.description}</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="space-y-2 ml-6">
                                      {lessonBlocks.map((block) => {
                                        const blockProgress = block.progress
                                        const isBlockCompleted = block.isCompleted || blockProgress?.status === 'COMPLETED'
                                        const isBlockStarted = block.isStarted || blockProgress?.status === 'IN_PROGRESS'
                                        
                                        return (
                                          <div
                                            key={block.id}
                                            className={`flex items-center justify-between p-2.5 transition-colors border ${
                                              isBlockCompleted
                                                ? 'bg-[#E8C547]/10 border-[#E8C547]/50'
                                                : isBlockStarted
                                                ? 'bg-[#203a8a]/5 border-[#203a8a]/30'
                                                : 'bg-[#FAFBFD] border-[#D5DAE3] hover:border-[#203a8a]/40'
                                            }`}
                                          >
                                            <div className="flex items-center gap-2">
                                              {getBlockIcon(block.type)}
                                              <div className="flex items-center gap-2">
                                                {isBlockCompleted ? (
                                                  <CheckSquare className="w-3 h-3 text-[#E8C547]" />
                                                ) : isBlockStarted ? (
                                                  <Circle className="w-3 h-3 text-[#203a8a]" />
                                                ) : null}
                                                <div>
                                                  <h6 className="text-sm text-[#1B2240]">{block.title}</h6>
                                                  {block.description && (
                                                    <p className="text-xs text-[#6B7280]">{block.description}</p>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]">
                                              <Clock className="w-3 h-3" />
                                              <span>{formatDuration(block.duration, 'minutes')}</span>
                                            </div>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                )
                              })}
                              {/* Fallback pour l'ancienne structure (blocs directement dans le module) */}
                              {(!module.lessons || module.lessons.length === 0) && (module.blocks || []).map((block) => {
                                const blockProgress = block.progress
                                const isBlockCompleted = block.isCompleted || blockProgress?.status === 'COMPLETED'
                                const isBlockStarted = block.isStarted || blockProgress?.status === 'IN_PROGRESS'
                                
                                return (
                                  <div
                                    key={block.id}
                                    className={`flex items-center justify-between p-3 transition-colors border ${
                                      isBlockCompleted
                                        ? 'bg-[#E8C547]/10 border-[#E8C547]/50'
                                        : isBlockStarted
                                        ? 'bg-[#203a8a]/5 border-[#203a8a]/30'
                                        : 'bg-[#FAFBFD] border-[#D5DAE3] hover:border-[#203a8a]/40'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      {getBlockIcon(block.type)}
                                      <div className="flex items-center gap-2">
                                        {isBlockCompleted ? (
                                          <CheckSquare className="w-4 h-4 text-[#E8C547]" />
                                        ) : isBlockStarted ? (
                                          <Circle className="w-4 h-4 text-[#203a8a]" />
                                        ) : null}
                                        <div>
                                          <h5 className="font-medium text-[#1B2240]">{block.title}</h5>
                                          {block.description && (
                                            <p className="text-sm text-[#6B7280]">{block.description}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]">
                                      <Clock className="w-3 h-3" />
                                      <span>{formatDuration(block.duration, 'minutes')}</span>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  )})}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Right Column */}
          <div className="lg:col-span-1">
            <div className="sticky top-16 sm:top-20 md:top-6 lg:top-8">
              {/* Enrollment Info */}
              {enrollment && (
                <Card className="mb-4 sm:mb-6 border border-[#D5DAE3] shadow-none rounded-none bg-white">
                  <CardHeader className="p-4 sm:p-5 md:p-6 pb-3 sm:pb-4">
                    <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Inscription</span>
                    <CardTitle className="font-display text-xl text-[#203a8a] mt-1">{tObj.courseDetail.enrollmentInfo()}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 md:p-6 pt-0">
                    <ul className="divide-y divide-[#D5DAE3] border-t border-[#D5DAE3]">
                      <li className="flex items-center justify-between py-3">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{tObj.courseDetail.status()}</span>
                        <span className={`text-[10px] uppercase tracking-[0.22em] font-semibold px-2 py-1 ${
                          enrollment.status === 'ENROLLED' ? 'bg-[#203a8a] text-white' :
                          enrollment.status === 'COMPLETED' ? 'bg-[#E8C547] text-[#203a8a]' :
                          'bg-[#F1F3F8] text-[#1B2240]'
                        }`}>
                          {enrollment.status === 'ENROLLED' ? tObj.courseDetail.enrolled() :
                           enrollment.status === 'COMPLETED' ? tObj.courseDetail.completedStatus() :
                           enrollment.status}
                        </span>
                      </li>
                      {enrollment.enrolledAt && (
                        <li className="flex items-center gap-2 py-3 text-sm">
                          <Calendar className="w-3.5 h-3.5 text-[#E8C547] flex-shrink-0" />
                          <span className="text-[#1B2240]/70">
                            {tObj.courseDetail.enrolledOnDate()} {new Date(enrollment.enrolledAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </span>
                        </li>
                      )}
                      {enrollment.completedAt && (
                        <li className="flex items-center gap-2 py-3 text-sm">
                          <Award className="w-3.5 h-3.5 text-[#E8C547] flex-shrink-0" />
                          <span className="text-[#1B2240]/70">
                            {tObj.courseDetail.completedOnDate()}{new Date(enrollment.completedAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </span>
                        </li>
                      )}
                      {enrollment.accessExpiresAt && (
                        <li className="flex items-center gap-2 py-3 text-sm">
                          <Clock className="w-3.5 h-3.5 text-[#E8C547] flex-shrink-0" />
                          <span className="text-[#1B2240]/70">
                            {tObj.courseDetail.expiredOnDate()}{new Date(enrollment.accessExpiresAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </span>
                        </li>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Course Info - Desktop seulement */}
              <Card className="hidden lg:block border border-[#D5DAE3] shadow-none rounded-none bg-white">
                <CardHeader className="p-5 sm:p-5 md:p-6 pb-4 sm:pb-4">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Infos</span>
                  <CardTitle className="font-display text-xl text-[#203a8a] mt-1">{tObj.courseDetail.courseInfo()}</CardTitle>
                </CardHeader>
                <CardContent className="p-5 md:p-6 pt-0">
                  <ul className="divide-y divide-[#D5DAE3] border-t border-[#D5DAE3]">
                    {availableLanguages.length > 0 && (
                      <li className="flex items-start gap-3 py-3">
                        <Languages className="w-4 h-4 text-[#E8C547] mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B7280]">
                            {tObj.courseDetail.availableLanguages()}
                          </span>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {availableLanguages.map((lang) => (
                              <span
                                key={lang}
                                className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 border border-[#D5DAE3] text-[#1B2240]"
                              >
                                {lang === 'fr' ? 'FR' : lang === 'en' ? 'EN' : lang.toUpperCase()}
                              </span>
                            ))}
                          </div>
                        </div>
                      </li>
                    )}
                    <li className="flex items-center gap-3 py-3 text-sm">
                      <Calendar className="w-4 h-4 text-[#E8C547] flex-shrink-0" />
                      <span className="text-[#1B2240]/80">{tObj.courseDetail.lastUpdate()}{new Date(course.updatedAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                    </li>
                    <li className="flex items-center gap-3 py-3 text-sm">
                      <Award className="w-4 h-4 text-[#E8C547] flex-shrink-0" />
                      <span className="text-[#1B2240]/80">{tObj.courseDetail.certificateIncluded()}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal - Afficher seulement si conditions remplies */}
      {showPaymentModal && course && !isFreeCourse(course) && isAuthenticated && !isEnrolled && (
        <CoursePaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            if (typeof setShowPaymentModal === 'function') {
              setShowPaymentModal(false)
            }
          }}
          course={course}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      {/* Auth Modal for non-authenticated users */}
      {showAuthModal && !isAuthenticated && course && course.id && courseId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              if (typeof setShowAuthModal === 'function') {
                setShowAuthModal(false)
              }
            }}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white border border-[#D5DAE3] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto z-10">
            {/* Header */}
            <div className="relative p-6 md:p-8 text-center border-b border-[#D5DAE3]">
              <button
                onClick={() => typeof setShowAuthModal === 'function' && setShowAuthModal(false)}
                className="absolute top-4 right-4 p-2 text-[#6B7280] hover:text-[#203a8a] transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="flex items-center justify-center mb-3">
                <div className="w-12 h-12 border border-[#203a8a] flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-[#203a8a]" />
                </div>
              </div>

              <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Accès requis</span>
              <h2 className="font-display text-2xl sm:text-3xl text-[#203a8a] mt-2 mb-3">
                {tObj.courseDetail.loginToAccess()}
              </h2>
              <p className="text-sm sm:text-base text-[#1B2240]/70 max-w-md mx-auto">
                {tObj.courseDetail.createAccountOrLogin()}
              </p>
            </div>

            {/* Course Preview */}
            {course && (
              <div className="p-5 md:p-6 border-b border-[#D5DAE3] bg-[#F1F3F8]">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Cours sélectionné</span>
                <div className="flex items-center gap-4 mt-2">
                  {course.thumbnail && (
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="w-20 h-20 object-cover flex-shrink-0 border border-[#D5DAE3]"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg text-[#203a8a] mb-1 truncate">{course.title}</h3>
                    {isFreeCourse(course) ? (
                      <span className="bg-[#E8C547] text-[#203a8a] text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 inline-block font-semibold">
                        {tObj.courseDetail.free()}
                      </span>
                    ) : (
                      <span className="font-display text-xl text-[#203a8a]">
                        {formatPrice(course.price, course.currency)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="p-5 md:p-6">
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Inclus</span>
              <ul className="divide-y divide-[#D5DAE3] border-y border-[#D5DAE3] mt-2 mb-5">
                {[
                  tObj.courseDetail.fullCourseAccess(),
                  tObj.courseDetail.trackProgress(),
                  tObj.courseDetail.completionCertificate(),
                  tObj.courseDetail.lifetimeAccess(),
                ].map((label, i) => (
                  <li key={i} className="flex items-center gap-3 py-3 text-sm text-[#1B2240]">
                    <CheckSquare className="w-4 h-4 text-[#E8C547] flex-shrink-0" />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>

              {/* Action Buttons */}
              <div className="space-y-2 sm:space-y-3">
                <Button
                  onClick={() => {
                    if (!course || !course.id || !courseId) {
                      showErrorToast({ message: tObj.courseDetail.errorMessage() })
                      return
                    }
                    try {
                      saveEnrollmentIntent({
                        courseId: course.id,
                        courseTitle: course.title || '',
                        courseThumbnail: course.thumbnail || '',
                        isFree: isFreeCourse(course),
                        returnUrl: window.location.pathname
                      })
                      if (typeof navigate === 'function' && typeof getLoginUrlWithContext === 'function') {
                        navigate(getLoginUrlWithContext(courseId))
                      }
                      if (typeof setShowAuthModal === 'function') {
                        setShowAuthModal(false)
                      }
                    } catch (err) {
                      console.error('Error saving enrollment intent:', err)
                      showErrorToast({ message: tObj.courseDetail.errorMessage() })
                    }
                  }}
                  className="w-full h-12 bg-[#203a8a] hover:bg-[#1a2f6e] text-white text-xs uppercase tracking-[0.22em] rounded-none"
                >
                  {tObj.courseDetail.createAccount()}
                  <Play className="w-4 h-4 ml-2" />
                </Button>
                
                <Button
                  onClick={() => {
                    if (!course || !course.id || !courseId) {
                      showErrorToast({ message: tObj.courseDetail.errorMessage() })
                      return
                    }
                    try {
                      saveEnrollmentIntent({
                        courseId: course.id,
                        courseTitle: course.title || '',
                        courseThumbnail: course.thumbnail || '',
                        isFree: isFreeCourse(course),
                        returnUrl: window.location.pathname
                      })
                      if (typeof navigate === 'function' && typeof getLoginUrlWithContext === 'function') {
                        navigate(getLoginUrlWithContext(courseId))
                      }
                      if (typeof setShowAuthModal === 'function') {
                        setShowAuthModal(false)
                      }
                    } catch (err) {
                      console.error('Error saving enrollment intent:', err)
                      showErrorToast({ message: tObj.courseDetail.errorMessage() })
                    }
                  }}
                  variant="outline"
                  className="w-full h-12 text-xs uppercase tracking-[0.22em] rounded-none border-[#203a8a] text-[#203a8a]"
                >
                  {tObj.courseDetail.alreadyHaveAccount()}
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-3 sm:mt-4">
                {tObj.courseDetail.termsAndConditions()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Comments Section - En bas de la page */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-2">
        <CommentsSection courseId={courseId} />
      </div>
    </div>
    </AccessGuard>
  )
}
